/**
 * @alfin-crm/contracts — BullMQ Queue Names and Typed Payloads
 *
 * All queue names and job payload shapes. Worker processors validate
 * incoming jobs against these schemas to guarantee type safety.
 */
import { z } from 'zod';

/** Named queues */
export const QUEUE_NAMES = {
  CONTACT_IMPORT: 'contact-import',
  CAMPAIGN_ORCHESTRATION: 'campaign-orchestration',
  WHATSAPP_DELIVERY: 'whatsapp-delivery',
  WEBHOOK_EVENTS: 'webhook-events',
  EXPORTS: 'exports',
  MAINTENANCE: 'maintenance',
} as const;


export type QueueName = (typeof QUEUE_NAMES)[keyof typeof QUEUE_NAMES];

// ---------------------------------------------------------------------------
// contact-import queue
// ---------------------------------------------------------------------------
export const ContactImportPayloadSchema = z.object({
  companyId: z.string().cuid(),
  importJobId: z.string().cuid(),
  userId: z.string().cuid(),
  filePath: z.string(),
  columnMapping: z.record(z.string()),
  duplicatePolicy: z.enum(['skip', 'update', 'reject']).default('update'),
  /** When true, worker uses fixed loan-sheet column mapping instead of generic contact mapping */
  template: z.enum(['generic', 'loan_sheet']).default('generic'),
});
export type ContactImportPayload = z.infer<typeof ContactImportPayloadSchema>;

// ---------------------------------------------------------------------------
// campaign-orchestration queue
// ---------------------------------------------------------------------------
export const CampaignOrchestrationPayloadSchema = z.object({
  companyId: z.string().cuid(),
  campaignId: z.string().cuid(),
  requestedByUserId: z.string().cuid(),
  scheduledAt: z.string().datetime().optional(),
});
export type CampaignOrchestrationPayload = z.infer<typeof CampaignOrchestrationPayloadSchema>;

// ---------------------------------------------------------------------------
// whatsapp-delivery queue
// ---------------------------------------------------------------------------
export const WhatsAppInteractiveListSchema = z.object({
  kind: z.literal('list'),
  header: z.string().optional(),
  body: z.string(),
  button: z.string().default('View options'),
  sections: z.array(
    z.object({
      title: z.string(),
      rows: z.array(
        z.object({
          id: z.string(),
          title: z.string(),
          description: z.string().optional(),
        }),
      ),
    }),
  ),
});

export const WhatsAppInteractiveFlowSchema = z.object({
  kind: z.literal('flow'),
  header: z.string().optional(),
  body: z.string(),
  footer: z.string().optional(),
  flowId: z.string(),
  flowToken: z.string(),
  flowCta: z.string().default('Enter details'),
  mode: z.enum(['draft', 'published']).default('published'),
  screen: z.string().default('PERSONAL_INFO'),
});

export const WhatsAppDeliveryPayloadSchema = z.object({
  companyId: z.string().cuid(),
  campaignId: z.string().optional(), // optional for manual messages
  campaignContactId: z.string().optional(),
  messageId: z.string().cuid().optional(), // to update manual message status
  integrationId: z.string().cuid(),
  recipientPhone: z.string(), // E.164
  templateId: z.string().cuid().optional(),
  templateVariables: z.record(z.unknown()).optional(),
  content: z.string().optional(),
  interactive: z.union([WhatsAppInteractiveListSchema, WhatsAppInteractiveFlowSchema]).optional(),
  mediaAssetId: z.string().cuid().optional(),
  attempt: z.number().int().min(1).default(1),
});
export type WhatsAppDeliveryPayload = z.infer<typeof WhatsAppDeliveryPayloadSchema>;

// ---------------------------------------------------------------------------
// webhook-events queue
// ---------------------------------------------------------------------------
export const WebhookEventPayloadSchema = z.object({
  companyId: z.string().cuid(),
  integrationId: z.string().cuid(),
  webhookEventId: z.string(), // Meta's unique event ID
  eventType: z.string(), // e.g. 'messages', 'statuses'
  rawPayload: z.unknown(), // stored as-is for processing
  receivedAt: z.string().datetime(),
});
export type WebhookEventPayload = z.infer<typeof WebhookEventPayloadSchema>;



// ---------------------------------------------------------------------------
// exports queue
// ---------------------------------------------------------------------------
export const ExportPayloadSchema = z.object({
  companyId: z.string().cuid(),
  exportJobId: z.string().cuid(),
  requestedByUserId: z.string().cuid(),
  resource: z.enum(['contacts', 'transactions', 'campaigns', 'audit_logs']),
  filters: z.record(z.unknown()).optional(),
  format: z.enum(['csv', 'xlsx']).default('csv'),
});
export type ExportPayload = z.infer<typeof ExportPayloadSchema>;

// ---------------------------------------------------------------------------
// maintenance queue
// ---------------------------------------------------------------------------
export const MaintenancePayloadSchema = z.object({
  task: z.enum([
    'cleanup_expired_sessions',
    'cleanup_old_audit_logs',
    'reset_monthly_ai_counters',
    'sync_whatsapp_templates',
    'whatsapp_bot_nudge',
  ]),
  companyId: z.string().cuid().optional(), // if absent, runs globally
});
export type MaintenancePayload = z.infer<typeof MaintenancePayloadSchema>;
